import { X, UserPlus, Mail, Phone, Star, Edit2, Trash2 } from "lucide-react";

export default function AllContactPersonsModal({ 
  open, 
  onClose, 
  company, 
  persons = [],
  onAddPerson, 
  onEditPerson, 
  onDeletePerson, 
  onSetPrimary 
}) {
  if (!open || !company) return null;

  const getInitials = (firstName, lastName) => {
    const first = firstName?.charAt(0) || "";
    const last = lastName?.charAt(0) || "";
    return (first + last).toUpperCase() || "?";
  };

  const getFullName = (firstName, lastName) => {
    return `${firstName || ""} ${lastName || ""}`.trim();
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="flex flex-col w-full max-w-4xl bg-white shadow-2xl rounded-2xl" style={{ maxHeight: "85vh" }}>

        {/* Header */}
        <div className="flex items-center justify-between flex-shrink-0 px-6 py-4 border-b border-neutral-100">
          <div>
            <h2 className="text-[15px] font-bold text-neutral-900">Contact Persons</h2>
            <p className="text-[11px] text-neutral-400 mt-0.5">{company.company_name} · {persons.length} person{persons.length !== 1 ? "s" : ""}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onAddPerson}
              className="flex items-center gap-1.5 text-[12px] font-semibold px-3 h-8 rounded-lg border transition-colors"
              style={{ color: "#009DB9", borderColor: "#009DB9" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(0,157,185,0.06)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
              <UserPlus size={13} /> Add Person
            </button>
            <button onClick={onClose} className="flex items-center justify-center rounded-lg w-7 h-7 text-neutral-400 hover:bg-neutral-100">
              <X size={15} />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          {persons.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <UserPlus size={28} className="mb-3 text-neutral-300" />
              <p className="text-[13px] font-medium text-neutral-400">No contact persons yet</p>
              <button onClick={onAddPerson}
                className="mt-3 text-[12px] font-semibold px-4 h-8 rounded-lg text-white"
                style={{ background: "#009DB9" }}>
                Add First Person
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="sticky top-0 bg-neutral-50/95">
                <tr className="border-b border-neutral-100">
                  {["Name", "Contact", "Job Title", "Department", "Primary", "Actions"].map((h) => (
                    <th key={h} className="px-5 py-3 text-[11px] font-semibold text-neutral-400 uppercase tracking-wider text-left">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {persons.map((p) => {
                  const initials = getInitials(p.firstName, p.lastName);
                  const fullName = getFullName(p.firstName, p.lastName);
                  const email = p.email;
                  const phone = p.phone || p.mobile;
                  
                  return (
                    <tr key={p.id} className="transition-colors border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                            style={{ background: "#009DB9" }}>
                            {initials}
                          </div>
                          <span className="text-[13px] font-semibold text-neutral-900">{fullName || "—"}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex flex-col gap-0.5">
                          {email && (
                            <span className="text-[11px] text-neutral-500 flex items-center gap-1">
                              <Mail size={10} /> {email}
                            </span>
                          )}
                          {phone && (
                            <span className="text-[11px] text-neutral-500 flex items-center gap-1">
                              <Phone size={10} /> {phone}
                            </span>
                          )}
                          {!email && !phone && (
                            <span className="text-[11px] text-neutral-300">No contact info</span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-[12px] text-neutral-500">{p.jobTitle || "—"}</td>
                      <td className="px-5 py-3 text-[12px] text-neutral-500">{p.department || "—"}</td>
                      <td className="px-5 py-3">
                        {p.primaryContact ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#009DB9]">
                            <Star size={12} fill="#009DB9" /> Primary
                          </span>
                        ) : (
                          <button
                            onClick={() => onSetPrimary?.(p.id)}
                            className="text-[10px] font-medium text-neutral-400 hover:text-[#009DB9] transition-colors"
                          >
                            Set as primary
                          </button>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => onEditPerson?.(p)}
                            className="p-1 text-neutral-400 hover:text-[#009DB9] hover:bg-neutral-100 rounded transition-colors"
                            title="Edit"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`Delete ${fullName}?`)) {
                                onDeletePerson?.(p.id);
                              }
                            }}
                            className="p-1 transition-colors rounded text-neutral-400 hover:text-red-600 hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex-shrink-0 px-6 py-4 border-t border-neutral-100">
          <button onClick={onClose}
            className="w-full h-9 rounded-xl text-[13px] font-semibold border border-neutral-200 text-neutral-600 hover:bg-neutral-50 transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}